import type {
  CreateAgentAction,
  UpdateAgentAction,
} from '@monorepo/shared/schemas/agents'
import {
  ACTION_TYPE,
  DEFAULT_UNIT_LABEL,
} from '@monorepo/shared/schemas/common'
import { Loader2, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { toast } from '@/shared/lib/toast'
import {
  useCreateAgentAction,
  useUpdateAgentAction,
} from '../hooks/useAgentActions'
import { useAgent } from '../hooks/useAgents'
import { useAgentSignals } from '../hooks/useSignals'
import type { AgentActionFormData } from '../types/agent.types'
import { AgentActionDrawer } from './AgentActionDrawer'
import { AgentActionsTable } from './AgentActionsTable'
import { AgentDrawer } from './AgentDrawer'
import { AgentPreviewCard } from './AgentPreviewCard'

interface AgentDetailProps {
  agentId: string
  isNewlyCreated?: boolean
}

export function AgentDetail({
  agentId,
  isNewlyCreated = false,
}: AgentDetailProps) {
  const [showAddActionDrawer, setShowAddActionDrawer] = useState(false)
  const [editingAction, setEditingAction] = useState<
    AgentActionFormData | undefined
  >(undefined)
  const [showEditDrawer, setShowEditDrawer] = useState(false)

  // Fetch agent data from API - React Query manages cache and auto-updates on mutations
  const { data: agent, isLoading: agentLoading } = useAgent(agentId)

  // Use real API data for actions - use agentId directly instead of waiting for agent data
  const { actions, rawActions, counts, loading, error, deleteAction } =
    useAgentSignals(agentId)

  // Show action errors as toast notifications
  useEffect(() => {
    if (error) {
      toast.error(`Error loading actions: ${error}`)
    }
  }, [error])

  const handleAddAction = () => {
    setEditingAction(undefined)
    setShowAddActionDrawer(true)
  }

  const handleEditAction = (action: AgentActionFormData) => {
    setEditingAction(action)
    setShowAddActionDrawer(true)
  }

  // Real API mutations
  const createActionMutation = useCreateAgentAction()
  const updateActionMutation = useUpdateAgentAction()

  const handleSaveAction = async (
    actionData: AgentActionFormData,
    costSignalIds: string[]
  ) => {
    if (!agentId) {
      toast.error('Agent ID is required')
      return
    }

    try {
      if (editingAction?.id) {
        // Update existing action
        const updateActionPayload: UpdateAgentAction = {
          action_name: actionData.action_name,
          action_type: actionData.action_type,
          description: actionData.description,

          // Update billable signal (in-place)
          billable_signal: {
            signal_name: actionData.signal_name,
            signal_code: actionData.signal_code,
            aggregation_type: actionData.aggregation_type,
            unit_label: actionData.unit_label || DEFAULT_UNIT_LABEL,
            value_field: actionData.value_field || null,
            description: `Billable signal for ${actionData.action_name}`,
          },

          // Replace all cost signals
          cost_signal_ids: costSignalIds,

          // Update value metrics
          value: {
            fte_equivalent: actionData.fte_equivalent || null,
            revenue_per_unit: actionData.revenue_per_unit || null,
            default_price_cents: actionData.default_price_cents || null,
          },
        }

        await updateActionMutation.mutateAsync({
          agentId,
          actionId: editingAction.id,
          data: updateActionPayload,
        })
      } else {
        // Create new action with billable signal and cost signals
        const createActionPayload: CreateAgentAction = {
          action_name: actionData.action_name,
          action_type: actionData.action_type,
          description: actionData.description,

          // Billable signal (always created new)
          billable_signal: {
            signal_name: actionData.signal_name,
            signal_code: actionData.signal_code,
            aggregation_type: actionData.aggregation_type,
            unit_label: actionData.unit_label || DEFAULT_UNIT_LABEL,
            value_field: actionData.value_field,
            description: `Billable signal for ${actionData.action_name}`,
          },

          // Cost signals (link existing by IDs)
          cost_signal_ids: costSignalIds,

          // Value metrics (optional)
          value:
            actionData.fte_equivalent ||
            actionData.revenue_per_unit ||
            actionData.default_price_cents
              ? {
                  fte_equivalent: actionData.fte_equivalent,
                  revenue_per_unit: actionData.revenue_per_unit,
                  default_price_cents: actionData.default_price_cents,
                }
              : undefined,
        }

        await createActionMutation.mutateAsync({
          agentId,
          data: createActionPayload,
        })
      }

      setShowAddActionDrawer(false)
      setEditingAction(undefined)
    } catch {
      // Error handled by mutation hooks
    }
  }

  const handleCancelForm = () => {
    setShowAddActionDrawer(false)
    setEditingAction(undefined)
  }

  return (
    <div className='max-w-7xl mx-auto p-6 space-y-6'>
      {/* Agent Preview Card at Top */}
      <AgentPreviewCard
        agent={agent}
        isLoading={agentLoading}
        onEdit={() => setShowEditDrawer(true)}
      />

      {/* Actions Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-xl font-semibold text-slate-900 dark:text-slate-100'>
            Agent Actions
          </h2>
          <p className='text-sm text-slate-600 dark:text-slate-400 mt-1'>
            Configure actions to track and monetize your agent's activities
          </p>
          {isNewlyCreated && counts.total === 0 && (
            <div className='mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg'>
              <p className='text-sm text-blue-700 dark:text-blue-300'>
                Your agent is ready! Add your first action to start tracking
                activities.
              </p>
            </div>
          )}
        </div>
        <div className='flex items-center gap-4'>
          <div className='flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400'>
            <Badge
              variant='secondary'
              className='bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
            >
              {counts.billableSignals} Billable Signals
            </Badge>
            <Badge
              variant='secondary'
              className='bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
            >
              {counts.costSignals} Cost Signals
            </Badge>
          </div>
          <Button onClick={handleAddAction} disabled={loading}>
            <Plus className='w-4 h-4 mr-2' />
            Add Agent Action
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className='flex items-center justify-center py-12'>
          <Loader2 className='w-8 h-8 animate-spin text-slate-500' />
          <span className='ml-2 text-slate-600 dark:text-slate-400'>
            Loading actions...
          </span>
        </div>
      )}

      {/* Actions Table */}
      {!loading && (
        <AgentActionsTable
          actions={[...actions.task, ...actions.workflow, ...actions.outcome]}
          rawActions={rawActions}
          onEdit={handleEditAction}
          onDelete={deleteAction}
        />
      )}

      {/* Agent Edit Drawer */}
      <AgentDrawer
        agent={agent}
        open={showEditDrawer}
        onOpenChange={setShowEditDrawer}
      />

      {/* Add/Edit Action Drawer */}
      <AgentActionDrawer
        open={showAddActionDrawer}
        onOpenChange={setShowAddActionDrawer}
        initialAction={editingAction}
        defaultActionType={ACTION_TYPE.task}
        onSave={handleSaveAction}
        onCancel={handleCancelForm}
      />
    </div>
  )
}
